import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm, Controller, Control } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import { useSnackbar } from 'notistack';

import { styled } from '@mui/material/styles';
import { Button, Box, Divider, MenuItem, IconButton, TextField, Card } from '@mui/material';
import Modal from '@mui/material/Modal';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import LoadingButton from '@mui/lab/LoadingButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CloseIcon from '@mui/icons-material/Close';

import useLocales from 'hooks/useLocales';
import { updatePasswordAction } from '../../redux/slices/user';

const Boxm = styled(Box)({
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'flex-end'
});

const CardStyle = styled(Card)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  backgroundColor: 'background.paper',
  padding: theme.spacing(3),
  transform: 'translate(-50%, -50%)'
}));

const CloseButton = styled(IconButton)(() => ({
  position: 'absolute',
  right: 10,
  top: 10
}));

// 定义表单数据类型
type PasswordFormInputs = {
  oldPassword: string;
  newPassword: string;
  Confirm: string;
};

export default function PasswordModal(params: any) {
  const { PassModal, PasswordModalClose } = params;
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const {
    trans,
    currentLang: { value: currentLanguage }
  } = useLocales();

  // 是否查看原密碼
  const [lookOld, setLookOld] = useState(false);
  const [lookNew, setLookNew] = useState(false);
  const [Confirm, setConfirm] = useState(false);
  // 按鍵loading
  const [Load, setLoad] = useState(false);

  const FormSchemas = Yup.object().shape({
    oldPassword: Yup.string().required(trans('ModifySecret.OldPasswordIsRequired')),
    newPassword: Yup.string()
      .min(6, trans('ModifySecret.PasswordMustBeAtLeast6Characters'))
      .required(trans('ModifySecret.NewPasswordIsRequired')),
    Confirm: Yup.string().oneOf(
      [Yup.ref('newPassword'), null],
      trans('ModifySecret.PasswordsMustMatch')
    )
  });

  const {
    handleSubmit,
    reset,
    setValue,
    getValues,
    control,
    formState: { errors }
  } = useForm<PasswordFormInputs>({
    mode: 'onTouched',
    resolver: yupResolver(FormSchemas),
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      Confirm: ''
    }
  });

  const onSubmit = async (data: PasswordFormInputs) => {
    setLoad(true);
    const { oldPassword, newPassword } = data;
    try {
      const returnedValue = await dispatch(updatePasswordAction({ oldPassword, newPassword }));
      if ((returnedValue as unknown as number) === 1) {
        enqueueSnackbar(trans('ModifySecret.ModifySuccess'), { variant: 'success' });
      } else {
        enqueueSnackbar(trans('ModifySecret.ModifyFailure'), { variant: 'error' });
      }
      setLoad(false);
    } catch (error) {
      enqueueSnackbar(trans('ModifySecret.ModifyFailure'), { variant: 'error' });
      setLoad(false);
    }
    //清空默認值
    reset({
      oldPassword: '',
      newPassword: '',
      Confirm: ''
    });
  };

  useEffect(() => {
    if (PassModal) {
      reset({
        oldPassword: '',
        newPassword: '',
        Confirm: ''
      });
    }
  }, [PassModal]);

  return (
    <Box>
      <Modal
        open={PassModal}
        onClose={PasswordModalClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <CardStyle
          sx={{
            width: {
              xs: 350,
              sm: currentLanguage === 'english' ? 850 : 700
            }
          }}
        >
          <CloseButton onClick={PasswordModalClose}>
            <CloseIcon sx={{ fontSize: '26px' }} />
          </CloseButton>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ flexGrow: 1 }}>
              <Grid container spacing={2} sx={{ mb: 3, mt: 2 }}>
                <Grid
                  item
                  xs={4}
                  sm={currentLanguage === 'english' ? 3.5 : 3}
                  sx={{ lineHeight: '50px', justifyContent: 'flex-start' }}
                >
                  <Boxm>* {trans('ModifySecret.originalPassword')} :</Boxm>
                </Grid>
                <Grid item xs={7} sm={8} sx={{ justifyContent: 'flex-start' }}>
                  <Controller<PasswordFormInputs, 'oldPassword'>
                    name="oldPassword"
                    control={control as Control<PasswordFormInputs>}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        id="outlined-oldPassword"
                        variant="outlined"
                        type={lookOld ? 'text' : 'password'}
                        sx={{ width: { xs: '100%' } }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setLookOld(!lookOld)}>
                                {lookOld ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                        {...field}
                        error={Boolean(error)}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid
                  item
                  xs={4}
                  sm={currentLanguage === 'english' ? 3.5 : 3}
                  sx={{ lineHeight: '50px', justifyContent: 'flex-start' }}
                >
                  <Boxm>* {trans('ModifySecret.NewPassword')} :</Boxm>
                </Grid>
                <Grid item xs={7} sm={8} sx={{ justifyContent: 'flex-start' }}>
                  <Controller<PasswordFormInputs, 'newPassword'>
                    name="newPassword"
                    control={control as Control<PasswordFormInputs>}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        id="outlined-newPassword"
                        variant="outlined"
                        type={lookNew ? 'text' : 'password'}
                        sx={{ width: { xs: '100%' } }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setLookNew(!lookNew)}>
                                {lookNew ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                        {...field}
                        error={Boolean(error)}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid
                  item
                  xs={4}
                  sm={currentLanguage === 'english' ? 3.5 : 3}
                  sx={{ lineHeight: '50px', justifyContent: 'flex-start' }}
                >
                  <Boxm> * {trans('ModifySecret.ConfirmPassword')} :</Boxm>
                </Grid>
                <Grid item xs={7} sm={8} sx={{ justifyContent: 'flex-start' }}>
                  <Controller<PasswordFormInputs, 'Confirm'>
                    name="Confirm"
                    control={control as Control<PasswordFormInputs>}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        id="outlined-Confirm"
                        variant="outlined"
                        type={Confirm ? 'text' : 'password'}
                        sx={{ width: { xs: '100%' } }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setConfirm(!Confirm)}>
                                {Confirm ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                        {...field}
                        error={Boolean(error)}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Box sx={{ textAlign: 'center', pb: 1 }}>
                <LoadingButton type="submit" variant="contained" loading={Load}>
                  {trans('ModifySecret.SaveChanges')}
                </LoadingButton>
              </Box>
            </Box>
          </form>
        </CardStyle>
      </Modal>
    </Box>
  );
}
